import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { ArticleAvailabilityTabDataRes } from '../../../../services/article.service';
import { DeliveryPeriods } from '../popups/deliveryPeriods/deliveryPeriods.popup';
import { PeriodConditionsBox } from '../../../../../../shared/components/periodConditionsBox/periodConditionsBox.component';
import { Button, IconButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  FieldsRow,
  FieldsSection,
  FormCheckboxList,
  FormSelect,
} from '../../../../../../shared/components/form';
import { C_Procurement_Product_Kind } from '../../../../../../graphql/generatedModel';
import { availabilityTabState } from '../availability.state';
import { SSettingsIcon } from '../../lists/components/listsForm.component.tsx';
import { GotoSettingsIcon } from '../../../../../../svg/custom.svg.tsx';
import { useNavigate } from 'react-router-dom';

export const AvailabilityForm: FC<IAvailabilityForm> = ({ formMethods }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getValues, setValue, handleSubmit } = formMethods || {};
  const deliveryPeriodsPopup = useMemo(() => new DeliveryPeriods(), []);
  const { periods, periodsWithCustomWeekDays, vacations, weekDays } = getValues() || {};
  const initPeriodConditions = { periods, periodsWithCustomWeekDays, vacations, weekDays };
  const [periodConditions, setPeriodConditions] = useState(initPeriodConditions);

  const onSubmit = useCallback(() => {
    const {
      formState: { isValid, isDirty },
    } = formMethods;
    if (isValid && isDirty) {
      availabilityTabState.pub.save();
    }
  }, [formMethods]);

  useEffect(() => {
    deliveryPeriodsPopup.stream.state().subscribe((v) => {
      if (v?.action === 'save') {
        const dataForSave = v?.values?.periodConditions;
        const { periods, periodsWithCustomWeekDays, vacations, weekDays } = dataForSave || {};
        setValue('periods', periods, { shouldDirty: true });
        setValue('periodsWithCustomWeekDays', periodsWithCustomWeekDays, { shouldDirty: true });
        setValue('vacations', vacations, { shouldDirty: true });
        setValue('weekDays', weekDays, { shouldDirty: true });
        setPeriodConditions(dataForSave);
        availabilityTabState.pub.save();
      }
    });

    const unsubState = availabilityTabState.sub.state().subscribe(({ defaultValues }) => {
      setPeriodConditions(defaultValues);
    });
    return () => {
      deliveryPeriodsPopup.stream.unsubscribe();
      unsubState.unsubscribe();
    };
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <PeriodConditionsBox periodConditions={periodConditions} width='600px' />
        <div>
          <Button
            onClick={() => {
              deliveryPeriodsPopup.stream.emit('open', {
                values: {
                  periodConditions: { periods, periodsWithCustomWeekDays, vacations, weekDays },
                },
              });
            }}
            sx={{ mt: 2 }}
            variant='contained'
            color='primary'
            startIcon={<SettingsIcon />}
          >
            {t('article.availability_setting')}
          </Button>
        </div>
        <form noValidate onBlur={handleSubmit(onSubmit)}>
          <FieldsSection title={null}>
            <FormCheckboxList
              withDivider
              emptyListIndication={{
                title: t('article.assortment_groups_are_not_configured'),
                text: (
                  <Trans
                    style={{ display: 'flex' }}
                    i18nKey='common.click_button_above_to_configure'
                    components={{ icon: <SSettingsIcon /> }}
                  />
                ),
              }}
              headerRightOptions={
                <IconButton
                  sx={{ ml: 'auto', height: '40px' }}
                  onClick={() => navigate('/settings/configuration/article')}
                  title={t('common.settings')}
                >
                  <GotoSettingsIcon />
                </IconButton>
              }
              name='articleAssortimentGroupsCheckedState'
              controlLabel={'<' + t('article.allProductGroups') + '>'}
              minWidth='600px'
              minHeight='150px'
            />
          </FieldsSection>
          <FieldsRow>
            <FormSelect
              name='availabilityInProcurementKindId'
              options={Object.values(C_Procurement_Product_Kind).map((key) => ({
                id: key,
                label: t(`enums.${key}`),
              }))}
              label={t('article.availability_in_procurement_module')}
            />
          </FieldsRow>
        </form>
        <deliveryPeriodsPopup.Component />
      </FormProvider>
    </>
  );
};
interface IAvailabilityForm {
  formMethods: UseFormReturn<ArticleAvailabilityTabDataRes>;
}
